import {
    Engine,
    Scene,
    Label,
    Font,
    Color,
    Vector,
    vec,
    Keys,
    TextAlign,
    CoordPlane,
    Actor,
    ImageSource
} from "excalibur";
import { Resources } from "../resources";
import { AudioManager } from "../systems/AudioManager";

/**
 * InstructionsScene - Opening Instructions
 * 
 * Displays the opening instruction pages one at a time.
 * Users click/tap to advance to the next page.
 * After the last page, transitions to the game.
 */
export class InstructionsScene extends Scene {
    private currentPage: number = 0;
    private pageImages: ImageSource[] = [];
    private pageActor!: Actor;
    private glowActor!: Actor;
    private promptLabel!: Label;
    private resizeHandler: (() => void) | null = null;
    private activationTime: number = 0;
    private pulseTime: number = 0;

    override onInitialize(engine: Engine): void {
        // Store instruction page images in order
        this.pageImages = [
            Resources.OpeningInstructions1,
            Resources.OpeningInstructions2,
            Resources.OpeningInstructions3,
        ];

        // Create glow actor (must be added before pageActor to be behind it)
        this.glowActor = new Actor({
            pos: vec(0, 0),
            anchor: Vector.Half,
            z: -1
        });
        this.glowActor.transform.coordPlane = CoordPlane.Screen;
        this.glowActor.graphics.opacity = 0; // Start invisible
        this.add(this.glowActor);

        // Create actor to display instruction pages
        this.pageActor = new Actor({
            pos: vec(0, 0),
            anchor: Vector.Half,
            z: 0
        });
        this.pageActor.transform.coordPlane = CoordPlane.Screen;
        this.add(this.pageActor);

        // Prompt to continue
        this.promptLabel = new Label({
            text: 'TAP TO CONTINUE',
            pos: vec(0, 0),
            font: new Font({
                family: 'Courier New, monospace',
                size: 20,
                bold: true,
                color: Color.fromHex('#fbbf24'),
                textAlign: TextAlign.Center,
                shadow: { blur: 4, offset: vec(2, 2), color: Color.Black }
            }),
            anchor: Vector.Half,
            z: 10
        });
        this.promptLabel.transform.coordPlane = CoordPlane.Screen;
        this.add(this.promptLabel);

        // Click/tap to advance (with debounce)
        engine.input.pointers.primary.on('down', () => {
            const timeSinceActivation = Date.now() - this.activationTime;
            if (engine.currentSceneName === 'instructions' && timeSinceActivation > 300) {
                this.nextPage(engine);
            }
        });

        // Keyboard support for desktop
        engine.input.keyboard.on('press', (evt) => {
            if (engine.currentSceneName === 'instructions' &&
                (evt.key === Keys.Space || evt.key === Keys.Enter)) {
                this.nextPage(engine);
            }
        });
    }

    override onActivate(): void {
        this.activationTime = Date.now();
        this.currentPage = 0;
        this.showCurrentPage();
        this.updateLayout(this.engine);

        // Listen for resize
        this.resizeHandler = () => this.updateLayout(this.engine);
        this.engine.screen.events.on('resize', this.resizeHandler);

        // Continue playing menu music
        AudioManager.playMenuMusic();
    }

    override onDeactivate(): void {
        if (this.resizeHandler) {
            this.engine.screen.events.off('resize', this.resizeHandler);
            this.resizeHandler = null;
        }
    }

    override onPreUpdate(_engine: Engine, delta: number): void {
        this.pulseTime += delta;
        // Pulse glow opacity between 0.3 and 0.8
        const pulse = 0.55 + Math.sin(this.pulseTime / 400) * 0.25;
        if (this.glowActor) {
            this.glowActor.graphics.opacity = pulse;
        }
    }

    private nextPage(engine: Engine): void {
        this.currentPage++;
        this.activationTime = Date.now(); // Reset debounce timer

        if (this.currentPage >= this.pageImages.length) {
            // All pages shown, go to game
            engine.goToScene('game');
        } else {
            this.showCurrentPage();
        }
    }

    private showCurrentPage(): void {
        const currentImage = this.pageImages[this.currentPage];
        if (currentImage && currentImage.isLoaded()) {
            const sprite = currentImage.toSprite();
            this.pageActor.graphics.use(sprite);

            // Create a matching sprite for the glow
            const glowSprite = currentImage.toSprite();
            glowSprite.tint = Color.fromHex('#4ade80'); // Bright green
            this.glowActor.graphics.use(glowSprite);
        }
        this.updateLayout(this.engine);
    }

    private updateLayout(engine: Engine): void {
        const w = engine.drawWidth;
        const h = engine.drawHeight;

        // Center the page image and scale to fit
        if (this.pageActor) {
            this.pageActor.pos = vec(w / 2, h / 2);
            if (this.glowActor) {
                this.glowActor.pos = vec(w / 2, h / 2);
            }

            const currentImage = this.pageImages[this.currentPage];
            if (currentImage && currentImage.isLoaded()) {
                const imgWidth = currentImage.width;
                const imgHeight = currentImage.height;

                // Scale to fit screen while maintaining aspect ratio (contain)
                const scaleX = w / imgWidth;
                const scaleY = h / imgHeight;
                const scale = Math.min(scaleX, scaleY);

                this.pageActor.scale = vec(scale, scale);
                // Scale glow slightly larger to create a rim effect
                if (this.glowActor) {
                    this.glowActor.scale = vec(scale * 1.05, scale * 1.05);
                }
            }
        }

        // Position prompt at bottom of screen
        if (this.promptLabel) {
            this.promptLabel.pos = vec(w / 2, h - 50);
        }
    }
}
